import {useSelector} from 'react-redux'

import {selectUser} from 'features/User'
import {DOPE_PRIMARY_DOMAIN} from 'definitions/constant'
import {isDlpConfigureNotificationsEnabledForPrimaryDomain} from 'definitions/constant/dlpNotificationsTenantGate'
import {CAC_WARNING_WHITELISTED_TENANTS} from 'definitions/constant/geminiCACWarningWhitelist'
import {EnvironmentEnum} from 'interfaces'
import {useEntitlementsQuery} from 'features/Entitlements/hooks'
import {selectSSLInspectionDisabledInData} from 'features/Policies/policiesSelectors'

interface UseFeatureFlagReturnType {
  isDopeTenant: boolean
  isPaywalled: boolean
  developmentOnlyFeatureFlag: boolean
  testOnlyFeatureFlag: boolean
  stagingOnlyFeatureFlag: boolean
  deploymentOnlyFeatureFlag: boolean
  nonProductionFeatureFlag: boolean
  productionDopeFeatureFlag: boolean
  swgDlpBlock: boolean
  isSSLInspectionDisabled: boolean
  isDlpConfigureNotificationsEnabled: boolean
  isCACWarningEnabled: boolean
  isCACPersonalAIEnabled: boolean
}

/**
 * @description This hook is used to get the feature flag values
 */
export const useFeatureFlag = (): UseFeatureFlagReturnType => {
  const user = useSelector(selectUser)

  const isSSLInspectionDisabled = useSelector(selectSSLInspectionDisabledInData)
  const {casbFullAccess, swgDlpBlock, isLoading} = useEntitlementsQuery({
    enabled: false,
  })

  // true if the user does not have CASB full access and the entitlements are not loading
  const isPaywalled = !casbFullAccess && !isLoading

  const primaryDomain = user?.primaryDomain

  /**
   * Default global feature flags for the application.
   */
  const isDopeTenant = primaryDomain === DOPE_PRIMARY_DOMAIN

  const developmentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEVELOPMENT

  const testOnlyFeatureFlag = process.env.REACT_APP_ENV === EnvironmentEnum.TEST

  const deploymentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEPLOYMENT

  const stagingOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.STAGING

  const nonProductionFeatureFlag =
    process.env.REACT_APP_ENV !== EnvironmentEnum.PRODUCTION

  // true if the environment is production and primaryDomain is dope.security
  const productionDopeFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.PRODUCTION && isDopeTenant

  // Governs whether the configure notifications UI is visible on DLP RHP
  const isDlpConfigureNotificationsEnabled =
    isDlpConfigureNotificationsEnabledForPrimaryDomain(primaryDomain)

  // Governs whether Warning is a selectable state for Gemini and Copilot CAC controls
  const normalizedPrimaryDomain = (primaryDomain ?? '').toLowerCase()
  const isCACWarningWhitelistedTenant = CAC_WARNING_WHITELISTED_TENANTS.some(
    tenant => tenant.toLowerCase() === normalizedPrimaryDomain,
  )
  const isCACWarningEnabled =
    nonProductionFeatureFlag || isCACWarningWhitelistedTenant

  // Governs the "On personal accounts" Gemini/Copilot row in CAC Global Settings
  const isCACPersonalAIEnabled =
    nonProductionFeatureFlag || productionDopeFeatureFlag

  return {
    nonProductionFeatureFlag,
    isDopeTenant,
    developmentOnlyFeatureFlag,
    deploymentOnlyFeatureFlag,
    testOnlyFeatureFlag,
    stagingOnlyFeatureFlag,
    productionDopeFeatureFlag,
    isPaywalled,
    swgDlpBlock,
    isSSLInspectionDisabled,
    isDlpConfigureNotificationsEnabled,
    isCACWarningEnabled,
    isCACPersonalAIEnabled,
  }
}
